import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useAxios from 'axios-hooks';
import isEmpty from 'lodash.isempty';
import { DateRange } from 'react-date-range';
import cn from 'classnames';
import { format } from 'date-fns';
import uniqBy from 'lodash.uniqby';
import { sortBy } from 'lodash';
import { Link } from 'react-router-dom';

import { long, short } from '../common/formatting/date';
import {
	Button,
	DropdownButton,
	SelectRaw,
	TabPanel,
	Tab,
} from '../../../components';

import './styles.scss';
import AssessmentModal from './AssessmentModal';
import AcknowledgedRPMNotificationsTab from './AcknowledgedRPMNotificationsTab';

const getButtonTitle = ({ startDate, endDate }) => {
	if (!startDate) {
		return 'All Time';
	}
	return `${format(startDate, 'MM/dd/yyyy')} - ${format(
		endDate,
		'MM/dd/yyyy'
	)}`;
};

const AcknowledgedNotifications = ({ user }) => {
	const userRaw = user.toJSON();
	const [recordLimit, setRecordLimit] = useState(50);
	const [offset, setOffset] = useState(0);
	const [selectedPatientId, setSelectedPatientId] = useState(-1);
	const [dateRange, setDateRange] = useState([
		{ startDate: null, endDate: null, key: 'selection' },
	]);
	const [patientOptions, setPatientOptions] = useState([]);
	const [isDatePickerOpen, setDatePickerOpen] = useState(false);
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);
	const [isViewingAssessment, setViewingAssessment] = useState(false);
	const [selectedNotification, setSelectedNotification] = useState(null);

	const [{ data: assignmentOptions, loading: isLoadingAssignments }] = useAxios(
		{
			method: 'GET',
			url: `/provider_practices/${userRaw.provider_practice_id}/care_quest_group_assignments`,
			params: {
				filter: JSON.stringify({
					include: ['patient'],
				}),
			},
		}
	);
	const [{ data, loading: isLoading /* error */ }, getAckowledgedNotications] =
		useAxios({
			method: 'GET',
			url: `/provider_practices/${userRaw.provider_practice_id}/care-quest-read-dashboard`,
		});

	useEffect(() => {
		// setOffset(0);
		let patientId, dateStart, dateEnd;
		if (selectedPatientId > 0) {
			patientId = selectedPatientId;
		}
		if (startDate && endDate) {
			dateStart = startDate;
			dateEnd = endDate;
		}
		getAckowledgedNotications({
			params: {
				patientId,
				dateStart,
				dateEnd,
				recordLimit,
				offset,
			},
		});
	}, [recordLimit, selectedPatientId, startDate, endDate, offset]);

	useEffect(() => {
		if (!isLoadingAssignments && Array.isArray(assignmentOptions)) {
			setPatientOptions([
				{
					value: -1,
					label: 'All Patients',
				},
				...sortBy(
					uniqBy(
						assignmentOptions.map(({ patient }) => patient),
						'id'
					),
					['name_last', 'name_first']
				).map(({ id, name_last, name_first, birth_dttm }) => ({
					value: id,
					label: `${name_last}, ${name_first} (${short(birth_dttm)})`,
				})),
			]);
		}
	}, [assignmentOptions, isLoadingAssignments]);

	useEffect(() => {
		if (!isViewingAssessment) {
			setSelectedNotification(null);
		}
	}, [isViewingAssessment]);

	const handlePatientChosen = ({ target }) => {
		setSelectedPatientId(target.value);
		setOffset(0);
	};

	const handlePageSizeChange = (newLimit) => {
		setRecordLimit(newLimit);
		setOffset(0);
	};

	const handleApplyDates = () => {
		setStartDate(dateRange[0].startDate);
		setEndDate(dateRange[0].endDate);
		setOffset(0);
		setDatePickerOpen(false);
	};

	const handleResetDates = () => {
		setStartDate(null);
		setEndDate(null);
		setOffset(0);
		setDatePickerOpen(false);
	};

	const handlePrevPage = () => {
		const newOffset = offset - recordLimit;
		setOffset(newOffset < 0 ? 0 : newOffset);
	};

	const handleNextPage = () => {
		setOffset(offset + recordLimit);
	};

	const handleViewAssessment = (event, notificationResponse) => {
		event.preventDefault();
		setSelectedNotification(notificationResponse);
		setViewingAssessment(true);
	};

	return (
		<div className="content-wrapper acknowledged-notifications">
			<ol className="breadcrumb">
				<li>
					<Link to="/">Home</Link>
				</li>
				<li>Patient Notifications</li>
				<li className="active">Acknowledged</li>
			</ol>
			<AssessmentModal
				isOpen={isViewingAssessment}
				onClose={() => setViewingAssessment(false)}
				notificationResponse={selectedNotification}
				practice={userRaw.provider_practice}
			/>
			<TabPanel>
				<Tab id="questionnaire" title="Questionnaire">
					<div className="row">
						<div className="col-md-3">
							<SelectRaw
								options={patientOptions}
								selectedValue={selectedPatientId}
								onChange={handlePatientChosen}
							/>
						</div>
						<div className="col-md-6 text-center">
							<div
								className="btn-group"
								role="group"
								aria-label="Data page navigation"
							>
								<Button
									icon="arrow-left"
									onClick={handlePrevPage}
									disabled={offset === 0}
									altTitle="Previous"
								/>
								<DropdownButton
									title={`Showing ${recordLimit} per page`}
									containerClassName="btn-group"
									items={[
										{ title: '10', onClick: () => handlePageSizeChange(10) },
										{ title: '25', onClick: () => handlePageSizeChange(25) },
										{ title: '50', onClick: () => handlePageSizeChange(50) },
										{ title: '100', onClick: () => handlePageSizeChange(100) },
									]}
								/>
								<Button
									icon="arrow-right"
									onClick={handleNextPage}
									disabled={Array.isArray(data) && data.length < recordLimit}
									altTitle="Next"
								/>
							</div>
						</div>
						<div className="col-md-3">
							<div
								className={cn('dropdown', 'daterangepicker-dropdown', {
									open: isDatePickerOpen,
								})}
							>
								<Button
									title={getButtonTitle({ startDate, endDate })}
									onClick={() => setDatePickerOpen(!isDatePickerOpen)}
									toggle
									wide
								/>
								<div className="dropdown-menu dropdown-menu-right">
									<DateRange
										onChange={(item) => setDateRange([item.selection])}
										ranges={dateRange}
										moveRangeOnFirstSelection={false}
										maxDate={new Date()}
									/>
									<div className="daterangepicker-dropdown-footer">
										<div className="pull-right">
											<Button
												title="Cancel"
												large
												onClick={() => setDatePickerOpen(false)}
											/>
											<Button
												title="Apply"
												onClick={handleApplyDates}
												large
												primary
												className="ml"
											/>
										</div>
										<Button
											title="Reset"
											large
											onClick={handleResetDates}
											danger
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
					<table className="table table-striped">
						<thead>
							<tr>
								<th>Patient</th>
								<th>DOB</th>
								<th>Questionnaire</th>
								<th>Submitted On</th>
								<th>Read On</th>
								<th>Response</th>
								<th>Responded By</th>
								<th>Responded On</th>
								<th>Visit Recommended</th>
							</tr>
						</thead>
						<tbody>
							{isLoading && (
								<tr>
									<td colSpan={9}>Loading...</td>
								</tr>
							)}
							{!isLoading &&
								data.map((response) => (
									<tr key={response.care_quest_response_groupId}>
										<td>
											{response.name_last}, {response.name_first}{' '}
											{!isEmpty(response.gender) ? `(${response.gender})` : ''}
										</td>
										<td>{short(response.birth_dttm)}</td>
										<td>
											<a
												className="btn btn-link"
												href="#"
												onClick={(event) =>
													handleViewAssessment(event, response)
												}
											>
												{response.care_quest_group_name}
											</a>
										</td>
										<td>
											{!isEmpty(response.response_dttm) &&
												long(response.response_dttm)}
										</td>
										<td>
											{!isEmpty(response.provider_read_dttm) &&
												long(response.provider_read_dttm)}
										</td>
										<td>
											{isEmpty(response.provider_response) ? (
												<em>No reponse given</em>
											) : (
												response.provider_response
											)}
										</td>
										<td>
											{!isEmpty(response.provider_name_last)
												? `${response.provider_name_first} ${response.provider_name_last} ${response.provider_credentials}`
												: null}
										</td>
										<td>
											{!isEmpty(response.provider_response_dttm) &&
												long(response.provider_response_dttm)}
										</td>
										<td>
											{response.evisit_recommended > 0 && (
												<em className="fa fa-fw fa-check" />
											)}
										</td>
									</tr>
								))}
						</tbody>
					</table>
				</Tab>
				<AcknowledgedRPMNotificationsTab user={userRaw} id="rpm" title="RPM" />
			</TabPanel>
		</div>
	);
};

AcknowledgedNotifications.propTypes = {
	user: PropTypes.object.isRequired,
};

export default AcknowledgedNotifications;
